import { IcFileConfig, IcStatistics, IcTro } from '@assets/icons/40x40';
import { Box, For, HStack, Tabs, Text, useBreakpointValue, VStack } from '@chakra-ui/react';
import DataSheetCard from '@components/common/layout/DataSheetCard';
import DataSheetCardLink from '@components/common/layout/DataSheetCardLink';
import { useMapContext } from '@components/map/MapContext';
import PopupContainer from '@components/map/popup/common/PopupContainer';
import AutomatonSectionInformation from '@components/map/popup/infos/perimeter/AutomatonSectionInformation';
import { DraggablePanel } from '@components/ui/draggable';
import { Switch } from '@components/ui/switch';
import { ToastStatusEnum } from '@components/ui/toaster';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { useAutomatonRight } from '@hooks/useAutomatonRight';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { makeSelectPlatformsNamesByCode } from '@redux/config/config.selectors';
import { updatePopupByMapId } from '@redux/maps/maps.reducer';
import {
  selectAutomatonSegmentStatusByKey,
  selectLowerStatesByName,
  selectSectionStatusByName,
  selectUpperStatesByName,
} from '@redux/situation/monitoring.selectors';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import {
  usePostAutomatonCommandMutation,
  useUpdateSensorActivationMutation,
  useUpdateSensorMaintenanceMutation,
} from '@services/c2/c2.api';
import { getPlatformsCodesFromMarks } from '@utils/common.utils';
import {
  getAutomatonNodeName,
  getSectionIconStatus,
  getSectionNameAndSegments,
  isSectionActive,
  isSectionInMaintenance,
  isSegmentActive,
  sendRepulsionCommand,
} from '@utils/map/automatonSection.utils';
import { createSensorToast } from '@utils/sensors/sensors.utils';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { AutomatonCommandEnum, AutomatonSectionDefenceCommand } from '@/types/c2/c2.types';
import { Dictionary } from '@/types/commons/commons.types';
import { NotificationTypeEnum } from '@/types/data/data.types';
import { MapIdEnum } from '@/types/map.types';
import { AutomatonSectionInfoData, InfoComponentProps, PopupTypeEnum } from '@/types/popup.types';
import {
  AutomatonSectionData,
  AutomatonSectionStatusEnumWithMaintenance,
  AutomatonSegment,
  SpecificStateEnum,
  SubSensorTypeEnum,
} from '@/types/sensor/sensors';

import EmergencySensorPhoneNumber from '../sensors/EmergencySensorPhoneNumber';
import AutomatonSectionIcon from './AutomatonSectionIcon';

export default function AutomatonSectionInfo({
  mapConstraintsRef,
  automatonSectionData,
  top,
  left,
  onClose,
}: Readonly<InfoComponentProps<AutomatonSectionInfoData> & { automatonSectionData: AutomatonSectionData }>) {
  const { formatMessage } = useIntl();
  const { mapId } = useMapContext();
  const breakpointSize = useBreakpointValue<'md' | 'sm'>({ base: 'sm', '4k': 'md' });
  const replayMode = mapId === MapIdEnum.REPLAY;

  const [patchMaintenance] = useUpdateSensorMaintenanceMutation();
  const [patchActivation] = useUpdateSensorActivationMutation();
  const [postRepulsionCommand, { isLoading: isRepulsionCommandLoading }] = usePostAutomatonCommandMutation();
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const situationTime = useSelectorWithReplayMode(selectSituationTime);

  const { hasRolePeriSensorCommand, hasRoleSensorMaintenance, hasRoleRepulsionCommand, hasRoleDetectionCommand } =
    useAutomatonRight();

  const { automaton, cabinet, section } = automatonSectionData;
  const platformCodes = getPlatformsCodesFromMarks(automaton.marks);
  const selectPlatformNames = useMemo(makeSelectPlatformsNamesByCode, []);
  const platforms = useAppSelector((state) => selectPlatformNames(state, platformCodes), shallowEqual);

  const { name, segments } = getSectionNameAndSegments(section);

  const sectionStatus = useSelectorWithReplayMode(selectSectionStatusByName, name);
  const effectiveStatus = sectionStatus?.effectiveStatus ?? null;

  const upperSegment: AutomatonSegment | null = section.upperSegment ?? null;
  const lowerSegment: AutomatonSegment | null = section.lowerSegment ?? null;
  const lowerStatus: AutomatonSectionStatusEnumWithMaintenance | null = useSelectorWithReplayMode(
    selectAutomatonSegmentStatusByKey,
    {
      automaton: automaton.uniqueKey,
      cabinet: cabinet.uniqueKey,
      segment: lowerSegment ? lowerSegment.uniqueKey : '',
    },
  );
  const lowerStates: Dictionary<string[]> = useSelectorWithReplayMode(selectLowerStatesByName, name) || {};
  const upperStates: Dictionary<string[]> = useSelectorWithReplayMode(selectUpperStatesByName, name) || {};

  const isUnderMaintenance: boolean = isSectionInMaintenance(
    automatonSectionData.automaton,
    automatonSectionData.cabinet,
    automatonSectionData.section,
  );

  const isActive = isSectionActive(automaton, cabinet, section);

  const isSegmentCommandable =
    isSegmentActive(automaton, cabinet, lowerSegment) && sectionStatus?.specificState !== SpecificStateEnum.SLEEP;

  function handleSectionMaintenance() {
    const toastParams = {
      commandValues: { state: !isUnderMaintenance },
      command: 'sensors.commands.maintenanceCommand',
      name,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });
    patchMaintenance({
      id: automaton.id,
      site: automaton.site,
      appCode: automaton.appCode,
      maintenance: !isUnderMaintenance,
      type: SubSensorTypeEnum.AUTOMATON_SEGMENT,
      codes: segments.map((segment) => segment.code),
    })
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
  }

  function handleSectionActivation() {
    const toastParams = {
      commandValues: { state: !isActive },
      command: 'sensors.commands.activationCommand',
      name,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });
    patchActivation({
      id: automaton.id,
      site: automaton.site,
      appCode: automaton.appCode,
      active: !isActive,
      type: SubSensorTypeEnum.AUTOMATON_SEGMENT,
      codes: segments.map((segment) => segment.code),
    })
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
  }

  function handleSectionRepulsion(enableRepulsion: boolean) {
    const command = {
      type: AutomatonCommandEnum.AUTOMATON_SECTION_COMMAND_DEFENCE,
      sensorCode: automaton.code,
      cabinetId: cabinet.nodeId,
      segmentId: segments[0].nodeId,
      defence: enableRepulsion,
    } as AutomatonSectionDefenceCommand;

    sendRepulsionCommand(
      command,
      postRepulsionCommand,
      enableRepulsion,
      name,
      false,
      automaton.site,
      automaton.appCode,
    );
  }

  function handlePopup() {
    const upperIdentifier = upperSegment
      ? `${upperSegment.uniqueKey}-${upperSegment.nodeId}_${upperSegment.segmentType}`
      : undefined;
    const lowerIdentifier = lowerSegment
      ? `${lowerSegment.uniqueKey}-${lowerSegment.nodeId}_${lowerSegment.segmentType}`
      : undefined;

    const identifiers: string[] = [upperIdentifier, lowerIdentifier].filter((value) => value !== undefined);
    updatePopup({
      mapId,
      popup: {
        type: PopupTypeEnum.HISTORY,
        data: {
          identifiers,
          sensorType: SubSensorTypeEnum.AUTOMATON_SEGMENT,
          sensorName: name,
          type: NotificationTypeEnum.SEGMENT,
          openingDate: situationTime ? new Date(situationTime) : new Date(),
        },
        position: { x: left, y: top },
      },
    });
  }

  const defenceActions = [
    {
      action: 'detection',
      i18nKey: 'automatonSection.tab.commands.defence.detection',
      disabled: !hasRoleRepulsionCommand,
    },
    {
      action: 'repulsion',
      i18nKey: 'automatonSection.tab.commands.defence.repulsion',
      disabled: !hasRoleRepulsionCommand,
    },
  ];

  return (
    <DraggablePanel.Root
      key={`${upperSegment?.code}-${lowerSegment?.code}`}
      constraintsRef={mapConstraintsRef}
      top={top}
      left={left}
      offsetY={-100}
    >
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="automatonSection.title" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width={{ base: '460px', '4k': '580px' }}>
        <HStack
          width="100%"
          gap="standardS"
          height={{ base: '104px', '4k': '120px' }}
          backgroundColor="neutral.700"
          paddingX="standardM"
        >
          <Box flexShrink={0} width="min-content">
            <AutomatonSectionIcon
              status={getSectionIconStatus(automatonSectionData, effectiveStatus)}
              state={sectionStatus?.specificState ?? null}
              size={breakpointSize}
            />
          </Box>
          <VStack width="100%" gap={0.5} alignItems="start">
            <Text textStyle="screenStd" paddingX={0.5} paddingY={0.25} backgroundColor="neutral.black" lineClamp={2}>
              {name}
            </Text>
            <HStack maxWidth="100%" gap={0.5}>
              <For each={platforms}>
                {(platform) => (
                  <Tooltip key={platform} content={platform} openDelay={200}>
                    <Text
                      paddingX={0.25}
                      backgroundColor="neutral.600"
                      textTransform="uppercase"
                      textStyle="screenStd"
                      fontWeight="medium"
                      color="neutral.200"
                    >
                      {platform.slice(0, 4)}
                    </Text>
                  </Tooltip>
                )}
              </For>
            </HStack>
          </VStack>
        </HStack>
        <Tabs.Root variant="neutral" width="100%" height="max-content" size="screenStd" defaultValue="information">
          <Tabs.List>
            <Tabs.Trigger value="information">
              <FormattedMessage id="automatonSection.tab.information.title" />
            </Tabs.Trigger>
            {hasRolePeriSensorCommand && (hasRoleDetectionCommand || hasRoleRepulsionCommand) && !replayMode && (
              <Tabs.Trigger value="commands">
                <FormattedMessage id="automatonSection.tab.commands.title" />
              </Tabs.Trigger>
            )}
            {hasRoleSensorMaintenance && (
              <Tabs.Trigger value="maintenance">
                <FormattedMessage id="automatonSection.tab.maintenance.title" />
              </Tabs.Trigger>
            )}
          </Tabs.List>
          <Tabs.ContentGroup>
            <Tabs.Content value="information">
              <AutomatonSectionInformation
                automatonSectionData={automatonSectionData}
                upperSegment={upperSegment}
                lowerSegment={lowerSegment}
                lowerStatus={lowerStatus}
              />
            </Tabs.Content>
            {hasRolePeriSensorCommand && (hasRoleDetectionCommand || hasRoleRepulsionCommand) && !replayMode && (
              <Tabs.Content value="commands">
                <VStack gap={0.25} width="100%" height="100%" textStyle="screenStd">
                  <DataSheetCard
                    icon={IcTro}
                    actions={defenceActions}
                    description={formatMessage({ id: 'automatonSection.tab.commands.defence.description' })}
                    isLoading={isRepulsionCommandLoading}
                    isDisabled={!effectiveStatus || !isSegmentCommandable}
                    onClick={(action) => handleSectionRepulsion(action === 'repulsion')}
                  />
                  <EmergencySensorPhoneNumber configuration={automaton} />
                </VStack>
              </Tabs.Content>
            )}
            {hasRoleSensorMaintenance && (
              <Tabs.Content value="maintenance">
                <VStack gap={0.25} width="100%" height="100%" textStyle="screenStd">
                  <PopupContainer>
                    <HStack width="100%" gap="standardXS">
                      <Text>
                        <FormattedMessage id="automatonSection.tab.maintenance.connect" />
                      </Text>
                      <HStack marginLeft="auto" gap={1}>
                        <Text textTransform="uppercase" textStyle="screenSm" color="neutral.300">
                          <FormattedMessage id={`global.${isActive ? 'on' : 'off'}`} />
                        </Text>
                        <Switch
                          size={breakpointSize}
                          checked={isActive}
                          onCheckedChange={handleSectionActivation}
                          disabled={replayMode}
                        />
                      </HStack>
                    </HStack>
                  </PopupContainer>
                  <PopupContainer>
                    <HStack width="100%" gap="standardXS">
                      <Text textStyle="screenStd">
                        <FormattedMessage id="automatonSection.tab.maintenance.maintenance" />
                      </Text>
                      <HStack marginLeft="auto" gap={1}>
                        <Text textTransform="uppercase" textStyle="screenSm" color="neutral.300">
                          <FormattedMessage id={`global.${isUnderMaintenance ? 'on' : 'off'}`} />
                        </Text>
                        <Switch
                          size={breakpointSize}
                          checked={isUnderMaintenance}
                          onCheckedChange={handleSectionMaintenance}
                          disabled={replayMode}
                        />
                      </HStack>
                    </HStack>
                  </PopupContainer>
                  <DataSheetCardLink
                    icon={IcFileConfig}
                    arrowOrientation="bottom"
                    label={formatMessage({ id: 'automatonSection.tab.maintenance.segmentDetails' })}
                  >
                    <VStack
                      padding="standardM"
                      backgroundColor="neutral.800"
                      width="100%"
                      maxHeight="250px"
                      className="popup-scrollbar"
                    >
                      <VStack gap={2} alignItems="start">
                        <VStack gap={1} alignItems="start">
                          <Text color="neutral.300">
                            <FormattedMessage id="automatonSection.segment.UPPER" />
                          </Text>
                          <VStack width="100%" gap={0.5} alignItems="start">
                            <For
                              each={Object.entries(upperStates)}
                              fallback={
                                <Text wordBreak="break-word">
                                  <FormattedMessage id="sensors.status.NA" />
                                </Text>
                              }
                            >
                              {([key, values]) => (
                                <VStack key={key} alignItems="start" marginLeft={2} gap={0.25}>
                                  <Text color="neutral.300">{key}</Text>
                                  {values && (
                                    <VStack alignItems="start" gap={0.25} marginLeft={2}>
                                      <For each={values.toSorted((a, b) => a.localeCompare(b))}>
                                        {(value) => (
                                          <Text key={value} wordBreak="break-word">
                                            {getAutomatonNodeName(value)}
                                          </Text>
                                        )}
                                      </For>
                                    </VStack>
                                  )}
                                </VStack>
                              )}
                            </For>
                          </VStack>
                        </VStack>
                        <VStack gap={1} alignItems="start">
                          <Text color="neutral.300">
                            <FormattedMessage id="automatonSection.segment.LOWER" />
                          </Text>
                          <VStack width="100%" gap={0.5} alignItems="start">
                            <For
                              each={Object.entries(lowerStates)}
                              fallback={
                                <Text wordBreak="break-word">
                                  <FormattedMessage id="sensors.status.NA" />
                                </Text>
                              }
                            >
                              {([key, values]) => (
                                <VStack key={key} alignItems="start" marginLeft={2} gap={0.25}>
                                  <Text color="neutral.300">{key}</Text>
                                  {values && (
                                    <VStack alignItems="start" gap={0.25} marginLeft={2}>
                                      <For each={values.toSorted((a, b) => a.localeCompare(b))}>
                                        {(value) => (
                                          <Text key={value} wordBreak="break-word">
                                            {getAutomatonNodeName(value)}
                                          </Text>
                                        )}
                                      </For>
                                    </VStack>
                                  )}
                                </VStack>
                              )}
                            </For>
                          </VStack>
                        </VStack>
                      </VStack>
                    </VStack>
                  </DataSheetCardLink>
                  <DataSheetCardLink
                    label={formatMessage({ id: 'contextmenu.actions.sectionHistory' })}
                    icon={IcStatistics}
                    onClick={handlePopup}
                  />
                  <EmergencySensorPhoneNumber configuration={automaton} />
                </VStack>
              </Tabs.Content>
            )}
          </Tabs.ContentGroup>
        </Tabs.Root>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
